'use client';

import { useEffect, useRef } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import {
  getResumableRecord,
  hasProducedStream,
  primeResumableRecord,
  RESUMABLE_STREAM_SET_EVENT,
  suppressSwitchBroadcast,
} from '@/lib/hitl/resumable-storage';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { useSyncStore } from '@/lib/sync/store';
import { reloadPage } from '@/lib/utils/reload-page';
import { findThread, setForceThreadRestore } from '@/lib/utils/thread';

const SWITCH_POLL_MS = 250;
const SWITCH_MAX_ATTEMPTS = 40; // ~10s

/**
 * Cross-tab live sync for resumable tenants. When a sibling starts a run, prime the resume record
 * and follow it onto the streaming thread by one of two paths:
 *
 * - SAME thread: assistant-ui caches the active thread's messages, so no in-place API re-fetches it.
 *   A true `reloadPage()` boots onto the primed thread and resumes the live stream exactly like a
 *   manual mid-stream refresh (the robust, verified resumable path).
 * - DIFFERENT thread: reload the thread list until the streaming thread is listed, then
 *   `switchToThread` by its local id (never `get()`-adopting an unlisted id, which 404s). The
 *   fresh-loaded thread has no cache, so the switch fetches history + attaches the resume.
 *
 * This tab bows out only of its OWN in-flight run (it's the producer, guarded by `hasProducedStream`).
 */
export function useCrossTabThreadSync(): void {
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const tenantName = useTenantName();
  const { emit, on } = useCrossTabSync();
  const api = useAui();
  const apiRef = useLatest(api);

  const isRunningRef = useLatest(useAuiState(state => state.thread.isRunning));
  const handledStreamIdsRef = useRef<Set<string>>(new Set());

  // Producer side: the transport dispatches the stream id as a module-scope window event (no
  // BroadcastChannel handle there); bridge it to the cross-tab channel so siblings can adopt it.
  useEffect(() => {
    if (!resumableEnabled || typeof window === 'undefined') return;
    const onStreamSet = (event: Event) => {
      const detail = (event as CustomEvent<{ streamId?: string; threadUuid?: string }>).detail;
      if (!detail?.streamId || !detail.threadUuid) return;
      emit(SYNC_EVENTS.RESUMABLE_STREAM_STARTED, {
        streamId: detail.streamId,
        threadUuid: detail.threadUuid,
      });
    };
    window.addEventListener(RESUMABLE_STREAM_SET_EVENT, onStreamSet);
    return () => window.removeEventListener(RESUMABLE_STREAM_SET_EVENT, onStreamSet);
  }, [resumableEnabled, emit]);

  useEffect(() => {
    if (!resumableEnabled) return;
    return on(SYNC_EVENTS.RESUMABLE_STREAM_STARTED, ({ streamId, threadUuid }) => {
      // Each stream is adopted once; the producer announces twice (send + header echo).
      if (handledStreamIdsRef.current.has(streamId)) return;
      handledStreamIdsRef.current.add(streamId);
      // Never adopt a stream THIS tab produced (would hijack the producer into a blank thread).
      // globalThis-backed, so it holds even when dev Fast Refresh splits the BroadcastChannel tab id.
      if (hasProducedStream(streamId) || isRunningRef.current) return;

      // Already adopted this stream — the record (sessionStorage) survives the same-thread reload,
      // whereas the in-memory handled-set doesn't. The producer announces the id twice (send +
      // header echo), so without this the reloaded receiver would reload a SECOND time.
      if (getResumableRecord(tenantName)?.streamId === streamId) return;
      primeResumableRecord({ tenantName, streamId, threadUuid });

      const threadsApi = apiRef.current.threads;
      const state = threadsApi.getState();
      const activeUuid = state.mainThreadId
        ? (findThread(state.threadItems, state.mainThreadId)?.remoteId ?? null)
        : null;

      // SAME thread: aui caches its messages, so only a full reload re-fetches. The record + boot
      // thread are primed, so the reloaded page resumes the live stream like a mid-stream refresh.
      if (activeUuid === threadUuid) {
        useSyncStore.getState().setThreadId(threadUuid);
        setForceThreadRestore();
        reloadPage();
        return;
      }

      // DIFFERENT thread: switch in place once it's listed (a thread not loaded in this tab has no
      // cache, so the switch fetches it fresh + attaches the resume). Reload the list until a
      // brand-new thread appears, then switch by local id (never `get()` an unlisted id → 404).
      // This tab is FOLLOWING the sibling, so suppress its own THREAD_SWITCH re-broadcast.
      let attempts = 0;
      const trySwitch = () => {
        attempts += 1;
        const listed = findThread(threadsApi.getState().threadItems, threadUuid);
        if (listed) {
          suppressSwitchBroadcast();
          try {
            threadsApi.switchToThread(listed.id);
          } catch {
            // swallow: switch is best-effort; the next tick retries
          }
          return;
        }
        threadsApi.reload().catch(() => {});
        if (attempts < SWITCH_MAX_ATTEMPTS) {
          setTimeout(trySwitch, SWITCH_POLL_MS);
        } else {
          handledStreamIdsRef.current.delete(streamId);
        }
      };
      trySwitch();
    });
  }, [resumableEnabled, on, tenantName, apiRef, isRunningRef]);
}
